/** Superadmin → Safety: 7am nudge + daily emailed record (recipients, time, send now). */
import { useState } from "react";
import { BellRing, Mail, Loader2, Send, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const Toggle = ({ on, onClick, testid }) => (
  <button onClick={onClick} aria-pressed={on} className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${on ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-600"}`} data-testid={testid}>{on ? "ON" : "OFF"}</button>
);

export default function SafetySchedules({ s, onChange }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const nudge = s.nudge || { enabled: false, time: "07:00" };
  const report = s.report || { enabled: false, time: "17:00", emails: [] };

  const save = async (patch, msg) => {
    try { const { data } = await api.put("/safety/settings", patch); onChange(data); if (msg) toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const addEmail = () => {
    const v = email.trim().toLowerCase();
    if (!v || !v.includes("@")) return toast.error("Enter a valid email");
    save({ report: { ...report, emails: [...report.emails.filter((e) => e !== v), v] } }); setEmail("");
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/safety/report/run");
      const em = data.email, cl = data.cloud;
      if (em?.emailed?.length) toast.success(`Record emailed to ${em.emailed.join(", ")}`);
      else if (em) toast.error(em.reason || `Could not email ${em.failed?.join(", ")}`);
      if (cl?.saved?.length) toast.success(`PDF saved to ${cl.saved.map((x) => (x.provider === "google" ? "Google Drive" : "OneDrive")).join(" + ")}`);
      else if (cl?.failed?.length) toast.error(`Drive copy failed: ${cl.failed.map((x) => x.error).join("; ")}`);
      else if (cl?.reason && !em) toast.message(`No email recipients and ${cl.reason}`);
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };
  const input = "rounded-md border border-gray-300 bg-transparent px-2 py-1 text-xs text-gray-900 dark:text-white";

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 p-4" data-testid="safety-nudge-section">
        <div className="flex flex-wrap items-center gap-3">
          <BellRing className="h-4 w-4 text-amber-500" />
          <div className="mr-auto min-w-[12rem] flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">Morning nudge</div>
            <p className="text-xs text-gray-500">Chat + push reminder to everyone on the "not yet confirmed" list at</p>
          </div>
          <input type="time" value={nudge.time} onChange={(e) => save({ nudge: { ...nudge, time: e.target.value } })} className={input} data-testid="safety-nudge-time" />
          <Toggle on={nudge.enabled} onClick={() => save({ nudge: { ...nudge, enabled: !nudge.enabled } }, nudge.enabled ? "Morning nudge OFF" : `Morning nudge ON at ${nudge.time}`)} testid="safety-nudge-toggle" />
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4" data-testid="safety-report-section">
        <div className="flex flex-wrap items-center gap-3">
          <Mail className="h-4 w-4 text-[#1a3fd1]" />
          <div className="mr-auto min-w-[12rem] flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">Daily emailed record</div>
            <p className="text-xs text-gray-500">Every day at the set time, email the day's record (every employee, IP, confirmed watched &amp; understood) with CSV + PDF attached.</p>
          </div>
          <input type="time" value={report.time} onChange={(e) => save({ report: { ...report, time: e.target.value } })} className={input} data-testid="safety-report-time" />
          <Toggle on={report.enabled} onClick={() => save({ report: { ...report, enabled: !report.enabled } }, report.enabled ? "Daily record OFF" : `Daily record ON at ${report.time}`)} testid="safety-report-toggle" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {report.emails.map((e) => (
            <span key={e} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-800 dark:bg-white/10 dark:text-white" data-testid={`safety-report-email-${e}`}>
              {e}<button onClick={() => save({ report: { ...report, emails: report.emails.filter((x) => x !== e) } })} aria-label={`Remove ${e}`} className="text-gray-500 hover:text-red-600"><X className="h-3 w-3" /></button>
            </span>
          ))}
          <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEmail()} placeholder="add email address" className={`${input} min-w-[12rem]`} data-testid="safety-report-email-input" />
          <button onClick={addEmail} className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:text-white/80" data-testid="safety-report-email-add">Add</button>
          <button onClick={sendNow} disabled={sending || (!report.emails.length && report.cloud === false)} className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-2.5 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="safety-report-send-now">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send today's record now
          </button>
        </div>
        {s.last_report_date && <p className="mt-2 text-[11px] text-gray-500" data-testid="safety-report-last">Last sent: {s.last_report_date}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-3">
          <div className="mr-auto min-w-[12rem] flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">Also save the PDF to the company drive</div>
            <p className="text-xs text-gray-500">At the same time the email goes out, the day's PDF + CSV land in "Company Portal / Safety records" on every linked OneDrive / Google Drive.</p>
          </div>
          <Toggle on={report.cloud !== false} onClick={() => save({ report: { ...report, cloud: report.cloud === false } }, report.cloud === false ? "Daily record will also be saved to the drive" : "Drive copy OFF")} testid="safety-report-cloud-toggle" />
        </div>
      </div>
    </div>
  );
}
